import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Calendar, MapPin, Users, ChevronRight, Check, Loader2, LogOut, RefreshCw } from 'lucide-react';
import { useApp, type AuthSessionSnapshot } from '../context/AppContext';
import { notify } from '../utils/notifications';
import { eventService } from '../services/dataService';
import { apiClient } from '../services/apiClient';
import type { ConferenceEvent } from '@conference/shared';

type Tab = 'upcoming' | 'past';

function formatDateRange(event: ConferenceEvent): string {
  const start = new Date(`${event.date_start}T00:00:00`);
  const end = new Date(`${event.date_end}T00:00:00`);
  const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr = end.toLocaleDateString('en-US', { day: 'numeric' });
  return `${startStr}–${endStr}, ${start.getFullYear()}`;
}

function statusTag(status: ConferenceEvent['status']): string {
  switch (status) {
    case 'live': return 'LIVE NOW';
    case 'published': return 'UPCOMING';
    case 'completed': return 'PAST';
    default: return status.toUpperCase();
  }
}

/** Wraps a promise with a timeout, rejecting if it takes longer than `ms`. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`Request timed out after ${ms}ms`)), ms)
    ),
  ]);
}

export default function EventSelectorScreen() {
  const navigate = useNavigate();
  const {
    setCurrentEvent,
    signOut,
    profile,
    authSession,
    isAuthSessionCurrent,
  } = useApp();

  const [tab, setTab] = useState<Tab>('upcoming');
  const [events, setEvents] = useState<ConferenceEvent[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const owner = authSession;
    if (!owner || !isAuthSessionCurrent(owner)) return;
    void loadEvents(owner);
  }, [authSession, isAuthSessionCurrent, tab]);

  async function loadEvents(owner: AuthSessionSnapshot) {
    if (!isAuthSessionCurrent(owner)) return;
    setLoading(true);
    setLoadError(false);
    try {
      const fetch =
        tab === 'upcoming'
          ? eventService.getUpcoming({ signal: owner.signal })
          : eventService.getPast({ signal: owner.signal });
      // Timeout after 10 s so the spinner never runs forever
      const data = await withTimeout(fetch, 10_000);
      if (!isAuthSessionCurrent(owner)) return;
      setEvents(data);
      if (data.length > 0) setSelected((prev) => prev ?? data[0].id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('Failed to load events:', msg, err);
      if (isAuthSessionCurrent(owner)) {
        setLoadError(true);
        notify.error(`Could not load events: ${msg}`);
      }
    } finally {
      if (isAuthSessionCurrent(owner)) setLoading(false);
    }
  }

  async function handleSignOut() {
    await signOut();
    navigate('/signin', { replace: true });
  }

  const selectedEvent = events.find((e) => e.id === selected);

  const handleContinue = async () => {
    const owner = authSession;
    if (
      !selected ||
      !selectedEvent ||
      !owner ||
      !isAuthSessionCurrent(owner)
    ) return;
    // Register the user for this event (idempotent — safe on repeat visits)
    try {
      await apiClient.post(
        `/events/${selected}/register`,
        {},
        { signal: owner.signal },
      );
      if (!isAuthSessionCurrent(owner)) return;
    } catch {
      if (!isAuthSessionCurrent(owner)) return;
      // Non-fatal — local state still works even if registration fails
    }
    if (!isAuthSessionCurrent(owner)) return;
    setCurrentEvent(selected);
    notify.success(`Welcome to ${selectedEvent.name}!`);
    navigate('/home');
  };

  return (
    <div className="size-full flex flex-col overflow-y-auto" style={{ background: 'var(--app-bg)' }}>
      {/* Header */}
      <div className="app-screen-header px-6 pt-14 pb-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-[var(--app-text)] text-[28px] tracking-tight">Select Event</h1>
            <p className="text-[var(--app-text-subtle)] text-sm mt-1">
              {profile?.name ? `Signed in as ${profile.name}` : 'Choose which conference to attend'}
            </p>
          </div>
          <button
            onClick={handleSignOut}
            className="mt-1 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors"
            style={{ color: 'var(--app-text-subtle)', background: 'var(--app-surface)' }}
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 px-6 mb-4">
        {(['upcoming', 'past'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className="px-4 py-2 rounded-full text-sm capitalize transition-colors"
            style={
              tab === t
                ? { background: 'var(--app-accent-solid)', color: '#fff' }
                : { background: 'var(--app-surface)', color: 'var(--app-text-subtle)' }
            }
          >
            {t}
          </button>
        ))}
      </div>

      {/* Event Cards */}
      <div className="flex flex-col gap-3 px-6 flex-1">
        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--app-accent)' }} />
            <p className="text-xs" style={{ color: 'var(--app-text-subtle)' }}>Loading events…</p>
          </div>
        ) : loadError ? (
          <div className="flex-1 flex flex-col items-center justify-center py-16 gap-3">
            <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>Could not connect to server</p>
            <button
              onClick={() => {
                const owner = authSession;
                if (owner && isAuthSessionCurrent(owner)) {
                  void loadEvents(owner);
                }
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm"
              style={{ background: 'var(--app-accent-dim)', color: 'var(--app-accent)' }}
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        ) : events.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-16 gap-2">
            <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>
              No {tab} events found
            </p>
            <p className="text-xs" style={{ color: 'var(--app-text-subtle)' }}>
              Check back later or contact your organizer
            </p>
          </div>
        ) : (
          events.map((event) => (
            <button
              key={event.id}
              onClick={() => setSelected(event.id)}
              className="relative rounded-xl overflow-hidden text-left transition-all active:scale-[0.98] border-2"
              style={{ borderColor: selected === event.id ? 'var(--app-accent-solid)' : 'transparent' }}
            >
              {/* Cover Image */}
              <div className="h-32 w-full overflow-hidden" style={{ background: 'var(--app-surface)' }}>
                {event.image_url ? (
                  <img src={event.image_url} alt={event.name} className="w-full h-full object-cover opacity-70" />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center"
                    style={{ background: 'var(--app-accent-dim)' }}
                  >
                    <Calendar className="w-10 h-10 opacity-30" style={{ color: 'var(--app-accent)' }} />
                  </div>
                )}
              </div>

              {/* Status Tag */}
              <div
                className="absolute top-3 left-3 text-white text-[10px] tracking-widest px-2 py-0.5 rounded"
                style={{
                  background:
                    event.status === 'live' ? '#ef4444' : 'var(--app-accent-solid)',
                }}
              >
                {statusTag(event.status)}
              </div>

              {/* Selected check */}
              {selected === event.id && (
                <div
                  className="absolute top-3 right-3 rounded-full size-6 flex items-center justify-center"
                  style={{ background: 'var(--app-accent-solid)' }}
                >
                  <Check className="w-3.5 h-3.5 text-white" />
                </div>
              )}

              {/* Info */}
              <div
                className="p-4 border-t"
                style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
              >
                <p className="text-[var(--app-text)] text-base">{event.name}</p>
                {event.description && (
                  <p className="text-xs mt-1 line-clamp-1" style={{ color: 'var(--app-text-subtle)' }}>
                    {event.description}
                  </p>
                )}
                <div className="flex items-center gap-1.5 mt-2 text-[var(--app-text-muted)] text-sm">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{formatDateRange(event)}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-1 text-[var(--app-text-muted)] text-sm">
                  <MapPin className="w-3.5 h-3.5" />
                  <span className="truncate">{event.location}</span>
                </div>
                {event.max_attendees && (
                  <div className="flex items-center gap-1.5 mt-1 text-[var(--app-text-muted)] text-sm">
                    <Users className="w-3.5 h-3.5" />
                    <span>{event.max_attendees.toLocaleString()}+ attendees</span>
                  </div>
                )}
              </div>
            </button>
          ))
        )}
      </div>

      {/* Continue Button */}
      <div className="px-6 py-6">
        <button
          onClick={handleContinue}
          disabled={!selected || loading}
          className="h-14 rounded-xl text-white text-lg w-full flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50"
          style={{
            background: 'var(--app-accent-solid)',
            boxShadow: '0px 10px 15px -3px var(--app-accent-glow)',
          }}
        >
          Continue
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
